import { useRootStore } from '@Shared/stores/root';
import { AuthToken } from '@Shared/types/auth';
import { createPinia, setActivePinia } from 'pinia';
import * as tau from 'tau-time';

import type { ApplicationInsights } from '@microsoft/applicationinsights-web';

const html2canvasImport = import('html2canvas');

// Function to round a number to a specified number of decimal places, rounding away from zero
export function roundAwayFromZero(num: string | number, decimalPlaces?: number) {
  // default decimal point is 0
  if (decimalPlaces == null || decimalPlaces == undefined || isNaN(decimalPlaces)) decimalPlaces = 0;
  const factorOfTen = Math.pow(10, decimalPlaces);
  // Shift value so that the decimal point boundary is the first decimal place, so that Math.round handles the rounding for us
  const roundedValue = Math.round(Math.abs(Number(num) * factorOfTen));
  // Shift value back
  return Math.sign(Number(num)) * (roundedValue / factorOfTen);
}

export function sleep(duration: tau.Duration | undefined) {
  return new Promise<void>((resolve) => setTimeout(resolve, duration));
}
// @NOTE: Don't use this on the root level of stores as it will break tests
// only use it in the implemented function when actually tracking events
export function useAppInsights(): ApplicationInsights {
  return window.appInsights as ApplicationInsights;
}

// Centralized function for tracking Application Insights events
// Supports optional prefix and product for different contexts (e.g., Frontline, Social, etc.)
export function trackAppInsightsEvent(
  name: string,
  properties: Record<string, any>,
  prefix?: string,
  product?: string
) {
  const appInsights = useAppInsights();

  // Apply prefix to event name if provided
  if (prefix) {
    name = prefix + name;
  }

  // Add product to properties if provided
  if (product) {
    properties.product = product;
  }

  appInsights.trackEvent({ name }, properties);
}

export function waitForSelector(selector: string): Promise<Element> {
  // needs to use old promise syntax since we want to resolve from *within* the observer
  return new Promise((resolve) => {
    let el = document.querySelector(selector);
    if (el) {
      resolve(el);
    }

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        // this is a little expensive (lots of events since we use childList & subtree)
        // so we want to make sure we disconnect as soon as we get the #app
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // to prevent *potential* race conditions we check again after observing
    // this isn't currently used in any tight code-paths (just mounting of application)
    // so this complexity is fine (since it's important we always mount) so we may adjust in future.
    el = document.querySelector(selector);
    if (el) {
      observer.disconnect();
      resolve(el);
    }
  });
}

export async function defineNewAppInsights(): Promise<ApplicationInsights> {
  const appInsights = new (await import('@microsoft/applicationinsights-web')).ApplicationInsights({
    config: {
      connectionString: import.meta.env.VITE_APP_INSIGHTS,
      disableTelemetry: import.meta.env.VITE_DISABLE_TELEMETRY === 'true',
    },
  });
  window.appInsights = appInsights;
  appInsights.loadAppInsights();

  return appInsights;
}

export function formUrlEncodingSerialization(obj: Record<string, string | number | boolean | null>) {
  const str = [];
  for (const p in obj) {
    const value = obj[p];
    if (value !== null) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(value));
    }
  }
  return str.join('&');
}

export async function wrapFallible<T>(
  executable: () => Promise<T>,
  onFail?: (error: unknown) => void
): Promise<T | null> {
  try {
    return await executable();
  } catch (e) {
    useAppInsights().trackException({
      exception: new Error(String(e)),
    });
    console.error(e);

    if (onFail) onFail(e);
    return null;
  }
}

export function setInterval(handler: () => void, duration?: tau.Duration): number {
  return window.setInterval(handler, duration?.milliseconds());
}

export function setTimeout(handler: () => void, duration?: tau.Duration): number {
  return window.setTimeout(handler, duration?.milliseconds());
}

export function debounce<TArgs extends any[]>(fn: (...args: TArgs) => void, delay: tau.Duration) {
  let timeoutID: number | undefined = undefined;

  return function (this: any, ...args: TArgs) {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => fn.apply(this, args), delay);
  };
}

export function debounceAsync<TArgs extends any[], TPromise>(
  fn: (...args: TArgs) => Promise<TPromise>,
  delay: tau.Duration
) {
  let timeoutID: number | undefined = undefined;
  let isRunning = false;
  let currentPromise: Promise<TPromise>;

  // this works very similarily to an asynchronous one but it recursively calls
  // debounceAsync while waiting for the response to return.
  // I want to avoid it chaining onto promises cause then it becomes a bit messy
  // (and ideally debounce requests should only be a couple seconds so this should be cheap)
  const debounce = function (this: any, ...args: TArgs) {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => {
      if (isRunning) {
        currentPromise.then(() => debounce(...args)).catch(() => debounce(...args));
      } else {
        isRunning = true;
        currentPromise = fn.apply(this, args);
        currentPromise.then(() => (isRunning = false)).catch(() => (isRunning = false));
      }
    }, delay);
  };
  return debounce;
}

// this is used by CCS to build up the logo and background string URL that can be used by a client
// if they want to show an additional logo and/or a different background
export function buildUrlForImage(imageName: string): string {
  return (
    import.meta.env.VITE_CDN_URL +
    '/CustomerCentreStage/' +
    useRootStore().clientId +
    '/' +
    useRootStore().token?.scope +
    '/' +
    imageName
  );
}

export async function takeScreenShot(blobCallBack: BlobCallback) {
  const canvas = await (await html2canvasImport).default(document.body);
  canvas.toBlob(blobCallBack, 'image/png');
}

if (import.meta.vitest) {
  describe('buildUrlImage', () => {
    beforeEach(() => {
      setActivePinia(createPinia());
    });
    it('Returns URL string for the image', () => {
      useRootStore().clientId = 'Demo';
      useRootStore().token = {
        // only adding in scope as others aren't needed
        scope: 'Admin',
      } as AuthToken;
      const expectedUrl = import.meta.env.VITE_CDN_URL + '/CustomerCentreStage/Demo/Admin';
      expect(expectedUrl + '/background').toEqual(buildUrlForImage('background'));
    });
  });
}
